import { useMemo } from 'react';
import { Box, Typography, Skeleton, Chip } from '@mui/material';
import {
  DataGridPremium,
  type GridColDef,
  type GridSortModel,
} from '@mui/x-data-grid-premium';
import { Link } from 'react-router-dom';

export interface DashboardTableColumn {
  field: string;
  label: string;
  width?: string;
  link?: string;
  display?: 'text' | 'badge';
  colorMap?: Record<string, string>;
}

export interface DashboardTableProps {
  displayName: string;
  data: Record<string, unknown>[];
  columns: DashboardTableColumn[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  loading?: boolean;
}

function interpolateLink(template: string, row: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(row[key] ?? ''));
}

export function DashboardTable({
  displayName,
  data,
  columns,
  sortBy,
  sortDirection = 'asc',
  limit,
  loading,
}: DashboardTableProps) {
  const safeData = Array.isArray(data) ? data : [];

  const gridColumns: GridColDef[] = useMemo(
    () =>
      columns.map((col) => {
        const colDef: GridColDef = {
          field: col.field,
          headerName: col.label,
          flex: col.width ? parseInt(col.width, 10) : 1,
        };

        if (col.display === 'badge') {
          colDef.renderCell = (params) => {
            const value = String(params.value ?? '');
            const color = col.colorMap?.[value];
            return (
              <Chip
                label={value}
                size="small"
                sx={color ? { backgroundColor: color, color: '#fff' } : undefined}
              />
            );
          };
        }

        if (col.link) {
          const linkTemplate = col.link;
          colDef.renderCell = (params) => {
            const href = interpolateLink(linkTemplate, params.row as Record<string, unknown>);
            const value = String(params.value ?? '');
            return (
              <Link to={href} style={{ color: 'inherit', textDecoration: 'underline' }}>
                {value}
              </Link>
            );
          };
        }

        return colDef;
      }),
    [columns],
  );

  const sortModel: GridSortModel | undefined = sortBy
    ? [{ field: sortBy, sort: sortDirection }]
    : undefined;

  const rows = useMemo(() => {
    const withIds = safeData.map((row, idx) => ({
      id: (row as Record<string, unknown>).id ?? idx,
      ...row,
    }));
    return limit ? withIds.slice(0, limit) : withIds;
  }, [safeData, limit]);

  if (loading) {
    return (
      <Box sx={{ p: 2, height: '100%' }}>
        <Skeleton width="40%" height={24} />
        <Skeleton variant="rectangular" height="calc(100% - 40px)" sx={{ mt: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {displayName}
      </Typography>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <DataGridPremium
          rows={rows}
          columns={gridColumns}
          density="compact"
          disableColumnMenu
          hideFooter
          sortModel={sortModel}
          disableRowSelectionOnClick
          sx={{ border: 'none' }}
        />
      </Box>
    </Box>
  );
}
